const BASE_CHAIN_ID = process.env.VUE_APP_BASE_CHAIN_ID || "0x2105"; // Base mainnet
const ETHEREUM_CHAIN_ID = "0x1"; // Ethereum mainnet

/**
 * Return whether a syntactically valid, complete JSON-RPC URL is configured.
 * Callers must only pass browser-safe public/proxied endpoints; provider URLs
 * without their required key fail closed here.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isUsableRpcUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;

  try {
    const parsedUrl = new URL(value.trim());
    if (!["http:", "https:"].includes(parsedUrl.protocol)) return false;

    const hostname = parsedUrl.hostname.toLowerCase();
    const pathname = parsedUrl.pathname.replace(/\/+$/, "");
    const isIncompleteInfuraUrl =
      hostname.endsWith("infura.io") && pathname === "/v3";
    const isIncompleteAlchemyUrl =
      hostname.endsWith("alchemy.com") && pathname === "/v2";
    const containsPlaceholder =
      /your[^/?#]*key|<[^>]+>|\{[^}]+\}/i.test(parsedUrl.href);

    return !(
      isIncompleteInfuraUrl ||
      isIncompleteAlchemyUrl ||
      containsPlaceholder
    );
  } catch (_error) {
    return false;
  }
}

/**
 * Resolve the first usable RPC URL without returning invalid configuration.
 *
 * @param {{ rpcUrls?: unknown[] } | null | undefined} chainParams
 * @returns {string|null}
 */
export function getPrimaryRpcUrl(chainParams) {
  const candidate = chainParams?.rpcUrls?.find(isUsableRpcUrl);
  return typeof candidate === "string" ? candidate.trim() : null;
}

// UnitCollectionFactory addresses (for per-user collections)
// Factory lets users deploy their own ERC-721 contracts with custom collection names
const COLLECTION_FACTORY_ADDRESSES = {
  "0x2105": process.env.VUE_APP_COLLECTION_FACTORY_BASE || "0xE6EfF0BbE9460812C99d61ce89ef8C8EA8EfAe0B", // Base mainnet - V2 with burn support - DEPLOYED Jan 20, 2026
  "0x1": process.env.VUE_APP_COLLECTION_FACTORY_ETHEREUM || "", // Ethereum mainnet - TODO: deploy
};

// V5 Contract addresses (current active contracts - contractURI support)
const CONTRACT_ADDRESSES_V5 = {
  "0x2105": "0x9b4fB2Ed51D541b80734f44b04C51878c42486d3", // Base mainnet V5.1 with burn support - DEPLOYED Jan 20, 2026
  "0x1": "0x30Aeba9a02C515911984e0d701100502CE72b56F", // Ethereum mainnet V5 - DEPLOYED Jan 11, 2026
};

// V4 Contract addresses (previous active contracts - empty baseURI, images work)
const CONTRACT_ADDRESSES_V4 = {
  "0x2105": "0x93BcDFbe414Ae51d10Cd057E68ab88830BaAc561", // Base mainnet V4 - DEPLOYED Oct 9, 2025
  "0x1": "0xc5dd92eac395a2a7da6f3d808b95384b803543e9", // Ethereum mainnet V4 - DEPLOYED Oct 9, 2025
};

// V3 Contract addresses (deprecated - server baseURI, images broken)
const CONTRACT_ADDRESSES_V3 = {
  "0x2105": "0x84607b9479f53d1B6316F1DA7DdE542C46A17520", // Base mainnet V3 - DEPRECATED (images broken)
  "0x1": "0x326744800d76e58fb6c3c360036a45eac630dd2d", // Ethereum mainnet V3 - DEPRECATED (images broken)
};

// V2 Contract addresses (deprecated - unverified)
const CONTRACT_ADDRESSES_V2 = {
  "0x2105": "0xDfe773aA184B7Eaf2D46d07743767aC880C18FA5", // Base mainnet V2 - DEPRECATED (unverified)
  "0x1": "0x6170f9054C98bc363373dBf9e13D1C5f69c48023", // Ethereum mainnet V2 - DEPRECATED
};

// V1 Contract addresses (legacy - 100% creator royalty)
const CONTRACT_ADDRESSES_V1 = {
  "0x2105": "0x969B423F6339206Bba38a04431852D6dB4810369", // Base mainnet V1 (legacy)
  "0x1": "0xb7912a27412d3d81b19c7fc9fdf837afbd28bed5", // Ethereum mainnet V1 (legacy)
};

// Active contract addresses (V5 for both chains)
const CONTRACT_ADDRESSES = {
  "0x2105": CONTRACT_ADDRESSES_V5["0x2105"], // Base V5
  "0x1": CONTRACT_ADDRESSES_V5["0x1"], // Ethereum V5
};

// Legacy fallback for backwards compatibility
const UNIT_MARKETPLACE_ADDRESS =
  process.env.VUE_APP_UNIT_MARKETPLACE_ADDRESS || CONTRACT_ADDRESSES["0x2105"];

const DEFAULT_TREASURY_ADDRESS = "0x82d67dAea96680CB585B6444B326C2f5B27Ce2dA";
const TREASURY_ADDRESS =
  process.env.VUE_APP_TREASURY_ADDRESS || DEFAULT_TREASURY_ADDRESS;
const MINT_FEE_WEI = process.env.VUE_APP_MINT_FEE_WEI || "30000000000000";
const PLATFORM_FEE_BPS = process.env.VUE_APP_PLATFORM_FEE_BPS || "200";
const SWAP_FEE_BPS = process.env.VUE_APP_SWAP_FEE_BPS || "20";
const PLATFORM_ROYALTY_SHARE_BPS =
  process.env.VUE_APP_PLATFORM_ROYALTY_SHARE_BPS || "3000";
const DEFAULT_ROYALTY_BPS =
  process.env.VUE_APP_UNIT_MARKETPLACE_DEFAULT_ROYALTY_BPS || "500";
const MAX_ROYALTY_BPS =
  process.env.VUE_APP_UNIT_MARKETPLACE_MAX_ROYALTY_BPS || "5000";

const parseBps = (value, fallback) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric < 0) {
    return fallback;
  }
  return numeric;
};

const parsedDefaultRoyaltyBps = parseBps(DEFAULT_ROYALTY_BPS, 500);
const parsedMaxRoyaltyBps = Math.min(parseBps(MAX_ROYALTY_BPS, 5000), 5000);

const normalizedDefaultRoyaltyBps = Math.min(
  parsedDefaultRoyaltyBps,
  parsedMaxRoyaltyBps
);

/**
 * Get the marketplace contract address for a specific chain
 * @param {string} chainId - Chain ID in hex format (e.g., "0x2105", "0x1")
 * @returns {string} Contract address for the specified chain
 */
export function getMarketplaceAddress(chainId) {
  if (typeof chainId === "number") chainId = "0x" + chainId.toString(16);
  const normalizedChainId = chainId?.toLowerCase();
  return CONTRACT_ADDRESSES[normalizedChainId] || UNIT_MARKETPLACE_ADDRESS;
}

/**
 * Get the collection factory address for a specific chain
 * @param {string} chainId - Chain ID in hex format (e.g., "0x2105", "0x1")
 * @returns {string|null} Factory address or null if not deployed
 */
export function getCollectionFactoryAddress(chainId) {
  if (typeof chainId === "number") chainId = "0x" + chainId.toString(16);
  const normalizedChainId = chainId?.toLowerCase();
  const address = COLLECTION_FACTORY_ADDRESSES[normalizedChainId];
  return address || null;
}

/**
 * Get contract version for a specific address
 * @param {string} contractAddress - Contract address to check
 * @returns {string} 'v1' | 'v2' | 'v3' | 'v4' | 'v5' | 'unknown'
 */
export function getContractVersion(contractAddress) {
  if (!contractAddress) return 'unknown';
  
  const normalized = contractAddress.toLowerCase();
  
  // Check V5 addresses (current active)
  if (Object.values(CONTRACT_ADDRESSES_V5).some(addr => addr?.toLowerCase() === normalized)) {
    return 'v5';
  }
  
  // Check V4 addresses (previous active)
  if (Object.values(CONTRACT_ADDRESSES_V4).some(addr => addr?.toLowerCase() === normalized)) {
    return 'v4';
  }
  
  // Check V3 addresses (deprecated - images broken)
  if (Object.values(CONTRACT_ADDRESSES_V3).some(addr => addr?.toLowerCase() === normalized)) {
    return 'v3';
  }
  
  // Check V2 addresses (deprecated)
  if (Object.values(CONTRACT_ADDRESSES_V2).some(addr => addr?.toLowerCase() === normalized)) {
    return 'v2';
  }
  
  // Check V1 addresses (legacy)
  if (Object.values(CONTRACT_ADDRESSES_V1).some(addr => addr?.toLowerCase() === normalized)) {
    return 'v1';
  }
  
  return 'unknown';
}

export const nftConfig = {
  chainId: BASE_CHAIN_ID,
  marketplaceAddress: UNIT_MARKETPLACE_ADDRESS, // Default to Base for backwards compatibility
  treasuryAddress: TREASURY_ADDRESS,
  mintFeeWei: MINT_FEE_WEI,
  platformFeeBps: Number(PLATFORM_FEE_BPS),
  platformFeeRecipient: TREASURY_ADDRESS,
  swapFeeBps: Number(SWAP_FEE_BPS),
  platformRoyaltyShareBps: Number(PLATFORM_ROYALTY_SHARE_BPS),
  defaultRoyaltyBps: normalizedDefaultRoyaltyBps,
  maxRoyaltyBps: parsedMaxRoyaltyBps,
  baseChainParams: {
    chainId: "0x2105",
    chainName: "Base",
    nativeCurrency: {
      name: "Ether",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: ["https://mainnet.base.org"],
    blockExplorerUrls: ["https://basescan.org"],
  },
  ethereumChainParams: {
    chainId: "0x1",
    chainName: "Ethereum Mainnet",
    nativeCurrency: {
      name: "Ether",
      symbol: "ETH",
      decimals: 18,
    },
    // Public read-only default. Contract monitoring verifies chain identity
    // and block freshness before use, then falls back server-side when needed.
    rpcUrls: ["https://ethereum.publicnode.com"],
    blockExplorerUrls: ["https://etherscan.io"],
  },
  // Expose contract addresses mapping
  contractAddresses: CONTRACT_ADDRESSES,
  contractAddressesV5: CONTRACT_ADDRESSES_V5,
  contractAddressesV4: CONTRACT_ADDRESSES_V4,
  contractAddressesV3: CONTRACT_ADDRESSES_V3,
  contractAddressesV2: CONTRACT_ADDRESSES_V2,
  contractAddressesV1: CONTRACT_ADDRESSES_V1,
  // Collection factory addresses (per-user collections)
  collectionFactoryAddresses: COLLECTION_FACTORY_ADDRESSES,
};

/**
 * Per-chain mint fee overrides
 * Both chains currently use the same fee; add overrides here as needed.
 */
const MINT_FEE_WEI_PER_CHAIN = {
  "0x2105": MINT_FEE_WEI,     // Base
  "0x1":    MINT_FEE_WEI,     // Ethereum
};

/**
 * Estimated collection-deployment gas cost by chain (in ETH).
 * These are rough estimates – Ethereum is significantly more expensive.
 */
const DEPLOY_ESTIMATE_ETH_PER_CHAIN = {
  "0x2105": "0.0003",   // Base  – very cheap L2
  "0x1":    "0.008",    // Ethereum mainnet – ~10-30× more gas
};

/**
 * Get the mint fee (in wei, as a string) for a specific chain.
 * Falls back to the global MINT_FEE_WEI if no per-chain value exists.
 * @param {string} chainId
 * @returns {string}
 */
export function getChainMintFeeWei(chainId) {
  const id = chainId?.toLowerCase();
  return MINT_FEE_WEI_PER_CHAIN[id] || MINT_FEE_WEI;
}

/**
 * Get the estimated collection-deployment cost (ETH string) for a chain.
 * @param {string} chainId
 * @returns {string}
 */
export function getDeployEstimateEth(chainId) {
  const id = chainId?.toLowerCase();
  return DEPLOY_ESTIMATE_ETH_PER_CHAIN[id] || "0.0003";
}

export function assertMarketplaceConfig(chainId = null) {
  const address = chainId ? getMarketplaceAddress(chainId) : nftConfig.marketplaceAddress;
  if (!address) {
    throw new Error(
      `Unit Marketplace contract address is not configured for chain ${chainId || 'default'}. Check nft.js configuration.`
    );
  }
}
